// The console's wording and the small decisions behind it, kept apart from the
// views so they can be tested without a browser. French only, for now.

// What the login page says under the QR code, by the state the server sent.
export function stateLine(state) {
    switch (state) {
        case 'scanned':
            return 'Code scanné — confirmez sur votre téléphone.'
        case 'approved':
            return 'Connexion confirmée…'
        case 'refused':
            return 'Connexion refusée sur le téléphone.'
        default:
            return "Ouvrez l'app Padelr : Espace Clubs → votre club → Connecter la console web, puis scannez ce code."
    }
}

// "Nouveau code dans 1:05" - how long the code on screen still works.
export function countdownText(expiresAtSeconds, nowMs) {
    const left = Math.max(0, Math.ceil(expiresAtSeconds - nowMs / 1000))
    const minutes = Math.floor(left / 60)
    const seconds = String(left % 60).padStart(2, '0')
    return `Nouveau code dans ${minutes}:${seconds}`
}

// The club the landing shows: the one chosen last time if the admin still
// administers it, otherwise the first. Rights are re-read on every call, so a
// remembered club can have gone since.
export function chooseClub(clubs, rememberedId) {
    if (!clubs || clubs.length === 0) return null
    const remembered = clubs.find(club => club.id === rememberedId)
    return remembered || clubs[0]
}

export function fullName(me) {
    return [me.first_name, me.last_name].filter(part => part && part.length > 0).join(' ')
}

// "jusqu'à 18:42" - when the 8-hour session ends, in the reader's time.
export function sessionEndText(expiresAtSeconds) {
    if (!expiresAtSeconds) return ''
    const end = new Date(expiresAtSeconds * 1000)
    const time = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    return `Session valable jusqu'à ${time}`
}

// ---------------------------------------------------------------------------
// Courts: the day grid and the month calendar.
//
// Dates travel as plain { year, month, day } (month 1-12), the way the server
// takes and gives them, and as "2026-10-24" / "2026-10" in the address bar.
// Arithmetic goes through UTC so no time zone or summer-time change can move
// a day: a date here is a day on the club's calendar, not an instant.

const pad2 = n => String(n).padStart(2, '0')

function utc(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day || 1))
}

function fromUtc(date) {
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

export function isoDay(parts) {
    return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`
}

export function isoMonth(parts) {
    return `${parts.year}-${pad2(parts.month)}`
}

// "2026-10-24" as a day, or null for anything that is not a real one -
// "2026-02-30" included.
export function parseIsoDay(text) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text || '')
    if (!match) return null
    const parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    const back = fromUtc(utc(parts))
    if (back.year !== parts.year || back.month !== parts.month || back.day !== parts.day) return null
    return parts
}

export function parseIsoMonth(text) {
    const match = /^(\d{4})-(\d{2})$/.exec(text || '')
    if (!match) return null
    const month = Number(match[2])
    if (month < 1 || month > 12) return null
    return { year: Number(match[1]), month }
}

// Today on the reader's calendar.
export function todayParts(now = new Date()) {
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}

export function addDays(parts, days) {
    const date = utc(parts)
    date.setUTCDate(date.getUTCDate() + days)
    return fromUtc(date)
}

export function addMonths(parts, months) {
    const index = parts.year * 12 + (parts.month - 1) + months
    return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

export function sameDay(a, b) {
    return !!a && !!b && a.year === b.year && a.month === b.month && a.day === b.day
}

// Where the console's address puts the reader: "?vue=jour&date=2026-10-21"
// or "?vue=mois&date=2026-10". Anything missing or unreadable falls back to
// the day view on today, so a mangled link still opens on something useful.
// A month view keeps the day it came from when there is one, and a day view
// reached with only a month opens on that month's first day - or today, when
// it is this month.
//
// The opening hours, "?vue=horaires", and the members, "?vue=adherents",
// are the club's whatever the day, so those views carry no date.
export function readConsoleRoute(vue, date, today) {
    if (vue === 'horaires') return { view: 'horaires' }
    if (vue === 'adherents') return { view: 'adherents' }
    if (vue === 'occupation') return { view: 'occupation' }
    const view = vue === 'mois' ? 'mois' : 'jour'
    const day = parseIsoDay(date)
    const month = parseIsoMonth(date)
    if (view === 'mois') {
        return { view, month: day ? { year: day.year, month: day.month } : month || { year: today.year, month: today.month } }
    }
    if (day) return { view, day }
    if (month) {
        const isThisMonth = month.year === today.year && month.month === today.month
        return { view, day: isThisMonth ? today : { ...month, day: 1 } }
    }
    return { view, day: today }
}

const DATE_FORMATS = {
    day: new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
    short: new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }),
    month: new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    stamp: new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
}

// "samedi 24 octobre 2026"
export function dayTitle(parts) {
    return DATE_FORMATS.day.format(utc(parts))
}

// "samedi 24 octobre" - inside a sentence that already says which year.
export function dayShort(parts) {
    return DATE_FORMATS.short.format(utc(parts))
}

// "octobre 2026"
export function monthTitle(parts) {
    return DATE_FORMATS.month.format(utc({ ...parts, day: 1 }))
}

// The grid's own time: "8:00", "14:30". From the minutes since midnight the
// server sends alongside "8:00 AM", which is a wire format and not for reading.
export function slotTime(startMinute) {
    const minutes = Number(startMinute) || 0
    return `${Math.floor(minutes / 60)}:${pad2(minutes % 60)}`
}

export function courtTitle(court) {
    const number = `Terrain ${court.court_number}`
    return court.court_name ? `${number} · ${court.court_name}` : number
}

export const BLOCK_KINDS = [
    { value: 'COURT_BOOKING_KIND_EVENT', label: 'Événement' },
    { value: 'COURT_BOOKING_KIND_TOURNAMENT', label: 'Tournoi' },
    { value: 'COURT_BOOKING_KIND_MAINTENANCE', label: 'Maintenance' },
]

export function kindLabel(kind) {
    const known = BLOCK_KINDS.find(k => k.value === kind)
    return known ? known.label : 'Blocage'
}

// Each kind of block its own colour, so a tournament across three courts reads
// as one thing at a glance.
const KIND_COLORS = {
    COURT_BOOKING_KIND_EVENT: 'purple',
    COURT_BOOKING_KIND_TOURNAMENT: 'teal',
    COURT_BOOKING_KIND_MAINTENANCE: 'orange',
}

// A séance de coaching holding a court, in its own colour: the club did not
// block it and cannot lift it.
const LESSON_COLOR = 'pink'

// "Cours collectif" or "Cours individuel"; "Cours" when the séance could not be
// read, which still holds the court.
export function lessonKindText(lesson) {
    if (!lesson) return 'Cours'
    return lesson.is_collective ? 'Cours collectif' : 'Cours individuel'
}

// "9:00 – 10:00", when the séance really runs - which is not the grid slot it
// is shown in. In the reader's time, like every instant the console shows.
export function lessonTimeText(lesson) {
    if (!lesson || !lesson.start_at) return ''
    const format = date => `${date.getHours()}:${pad2(date.getMinutes())}`
    const start = new Date(lesson.start_at * 1000)
    const end = new Date((lesson.start_at + lesson.duration_minutes * 60) * 1000)
    return `${format(start)} – ${format(end)}`
}

// "3/4 joueurs" - who holds a place in this séance, of how many it takes.
export function lessonPlacesText(lesson) {
    if (!lesson) return ''
    return `${lesson.num_enrolled}/${lesson.capacity} ${plural(lesson.capacity, 'joueur', 'joueurs')}`
}

// What a cell of the day grid says about its slot: a status dot and a line,
// or a token for a block, plus the block's own label beside it. Green is
// what can still be booked, amber a game still looking for players, blue a
// full one.
export function slotLook(slot) {
    switch (slot.state) {
        case 'CONSOLE_SLOT_STATE_FREE':
            return { text: 'Libre', dot: 'success' }
        case 'CONSOLE_SLOT_STATE_GAME_PENDING':
            return { text: 'Partie — en attente', dot: 'warning' }
        case 'CONSOLE_SLOT_STATE_GAME_CONFIRMED':
            return { text: 'Partie — confirmée', dot: 'accent' }
        case 'CONSOLE_SLOT_STATE_BLOCKED':
            return {
                text: kindLabel(slot.kind),
                token: KIND_COLORS[slot.kind] || 'gray',
                detail: slot.label || '',
                isVisible: !!slot.show_reason,
            }
        case 'CONSOLE_SLOT_STATE_LESSON':
            return { text: lessonKindText(slot.lesson), token: LESSON_COLOR, detail: (slot.lesson && slot.lesson.coach_name) || '' }
        case 'CONSOLE_SLOT_STATE_HELD':
            return { text: 'Réservé', token: 'gray' }
        default:
            return { text: 'Indisponible', dot: 'neutral' }
    }
}

function plural(count, one, many) {
    return count === 1 ? one : many
}

// What the admin picks is half-hours (lunar-falcon-2), so that is what the
// selection and the result count.
export function selectionText(count) {
    return `${count} ${plural(count, 'demi-heure sélectionnée', 'demi-heures sélectionnées')}`
}

// "30 min", "1 h", "1 h 30", "60 h", with the non-breaking spaces French
// puts between a number and its unit.
export function durationText(minutes) {
    const total = Math.max(0, Number(minutes) || 0)
    const hours = Math.floor(total / 60)
    const rest = total % 60
    if (hours === 0) return `${rest}\u00a0min`
    return rest === 0 ? `${hours}\u00a0h` : `${hours}\u00a0h\u00a0${pad2(rest)}`
}

// "30 min perdues", "1 h perdue": free time on a court that no player can
// book, left over at the end of a stretch too short for another slot. Empty
// when nothing is lost, so a court that loses nothing says nothing.
export function lostText(minutes) {
    const total = Number(minutes) || 0
    if (total <= 0) return ''
    // "1 h perdue" is one hour; "30 min" and "1 h 30" are several minutes.
    return `${durationText(total)} ${total === 60 ? 'perdue' : 'perdues'}`
}

// "10:30 – 12:00": when a slot runs.
export function slotRangeText(slot) {
    const start = Number(slot.start_minute) || 0
    const length = Number(slot.duration_minutes) || SLOT_MINUTES
    return `${slotTime(start)} – ${slotTime(start + length)}`
}

// What one half-hour of the day grid says: a free one that no player can book
// is "Inutilisable", hatched; otherwise what holds it, as slotLook says.
export function halfHourLook(half) {
    if (half.state === 'CONSOLE_SLOT_STATE_FREE' && half.is_lost) {
        return { text: 'Inutilisable', dot: 'neutral', isLost: true }
    }
    return slotLook(half)
}

// One court's column of the day grid, keyed by the row's minute: each hold is
// one cell spanning its half-hours, each free half-hour its own cell, and a
// free half-hour where a player's slot starts carries that slot, so the grid
// shows where the 1h30 slots fall once a block has moved them. A row covered
// by a hold above it maps to { covered: true }; a row the court has no
// half-hour for maps to nothing.
export function courtColumn(court) {
    const cells = new Map()
    const halves = court.half_hours || []
    const slotStarts = new Map()
    ;(court.slots || []).forEach(slot => {
        if (slot.state === 'CONSOLE_SLOT_STATE_FREE') slotStarts.set(slot.start_minute, slot)
    })

    for (let i = 0; i < halves.length; i++) {
        const half = halves[i]
        if (!half.booking_id) {
            cells.set(half.start_minute, { half, span: 1, slot: slotStarts.get(half.start_minute) || null })
            continue
        }
        let span = 1
        while (i + span < halves.length && halves[i + span].booking_id === half.booking_id) {
            cells.set(halves[i + span].start_minute, { covered: true })
            span++
        }
        cells.set(half.start_minute, { half, span, slot: null })
        i += span - 1
    }

    return cells
}

const SKIP_WORDS = {
    CONSOLE_SKIP_REASON_ALREADY_BOOKED: ['déjà réservée', 'déjà réservées'],
    CONSOLE_SKIP_REASON_PAST: ['passée', 'passées'],
    CONSOLE_SKIP_REASON_NOT_A_SLOT: ['hors horaires', 'hors horaires'],
    CONSOLE_SKIP_REASON_NOT_THIS_CLUB: ['autre club', 'autre club'],
}
const OTHER_SKIP = ['refusée', 'refusées']

// "8 demi-heures bloquées. 2 ignorées : déjà réservées." When the skipped
// half-hours were skipped for different reasons, each reason gets its count:
// "3 ignorées : 2 déjà réservées, 1 passée."
export function blockResultText(blockedCount, skipped) {
    const head = blockedCount === 0
        ? 'Aucune demi-heure bloquée.'
        : `${blockedCount} ${plural(blockedCount, 'demi-heure bloquée', 'demi-heures bloquées')}.`
    const list = skipped || []
    if (list.length === 0) return head

    const counts = new Map()
    list.forEach(slot => {
        const words = SKIP_WORDS[slot.reason] || OTHER_SKIP
        counts.set(words, (counts.get(words) || 0) + 1)
    })
    const ignored = `${list.length} ${plural(list.length, 'ignorée', 'ignorées')}`
    const reasons = counts.size === 1
        ? [...counts.keys()].map(words => plural(list.length, words[0], words[1]))
        : [...counts.entries()].map(([words, count]) => `${count} ${plural(count, words[0], words[1])}`)
    return `${head} ${ignored} : ${reasons.join(', ')}.`
}

// How full a day is, for the month calendar's dot: nothing to count, nothing
// taken, under half, half to four fifths, four fifths and more. Compared on
// the exact ratio, so 79.9 % is still "medium" and 80 % is "high".
export function occupancyLevel(taken, total) {
    if (!total) return 'closed'
    if (!taken) return 'empty'
    const ratio = taken / total
    if (ratio < 0.5) return 'low'
    if (ratio < 0.8) return 'medium'
    return 'high'
}

// The StatusDot variant for each level; "closed" has no dot at all.
export const OCCUPANCY_DOTS = {
    empty: 'neutral',
    low: 'success',
    medium: 'warning',
    high: 'error',
}

// "18 %", with the non-breaking space French puts before the sign.
export function percentText(taken, total) {
    if (!total) return '—'
    return `${Math.round((taken / total) * 100)}\u00a0%`
}

// "7 / 40 créneaux (18 %)"
export function occupancyText(taken, total) {
    if (!total) return 'Aucun créneau ce jour-là'
    return `${taken} / ${total} ${plural(total, 'créneau', 'créneaux')} (${percentText(taken, total)})`
}

// "7 h 30 / 60 h occupées (12 %) · 1 h perdue": the month's day in minutes
// (lunar-falcon-2), now that a 30-minute block and a 90-minute game are not
// the same "one slot".
export function occupancyMinutesText(held, open, lost) {
    if (!open) return 'Fermé ce jour-là'
    const head = `${durationText(held)} / ${durationText(open)} occupées (${percentText(held, open)})`
    const wasted = lostText(lost)
    return wasted ? `${head} · ${wasted}` : head
}

// The weeks of a month, Monday first, each seven days long; days of the
// neighbouring months are null.
export function monthWeeks(parts) {
    const first = utc({ ...parts, day: 1 })
    const length = new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate()
    const lead = (first.getUTCDay() + 6) % 7
    const cells = []
    for (let i = 0; i < lead; i++) cells.push(null)
    for (let day = 1; day <= length; day++) cells.push(day)
    while (cells.length % 7 !== 0) cells.push(null)
    const weeks = []
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
    return weeks
}

export const WEEKDAYS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche']

function normalizeLesson(lesson) {
    if (!lesson) return null
    return {
        lesson_id: lesson.lesson_id || '',
        series_id: lesson.series_id || '',
        is_collective: !!lesson.is_collective,
        coach_name: lesson.coach_name || '',
        label: lesson.label || '',
        start_at: Number(lesson.start_at) || 0,
        duration_minutes: Number(lesson.duration_minutes) || 0,
        num_enrolled: Number(lesson.num_enrolled) || 0,
        capacity: Number(lesson.capacity) || 0,
    }
}

// The server sends every field, defaults included, but a field it leaves out
// reads as its default anyway: no state is UNKNOWN, no kind is a game.
function normalizeSlot(slot) {
    return {
        start_time: slot.start_time || '',
        start_minute: Number(slot.start_minute) || 0,
        state: slot.state || 'CONSOLE_SLOT_STATE_UNKNOWN',
        kind: slot.kind || 'COURT_BOOKING_KIND_GAME',
        label: slot.label || '',
        booking_id: slot.booking_id || '',
        is_past: !!slot.is_past,
        lesson: normalizeLesson(slot.lesson),
        show_reason: !!slot.show_reason,
        duration_minutes: Number(slot.duration_minutes) || 0,
    }
}

function normalizeHalfHour(half) {
    return { ...normalizeSlot(half), duration_minutes: 30, is_lost: !!half.is_lost }
}

export function normalizeCourtDay(json) {
    return ((json && json.courts) || []).map(court => ({
        court_id: court.court_id || '',
        court_number: Number(court.court_number) || 0,
        court_name: court.court_name || '',
        slots: (court.slots || []).map(normalizeSlot),
        half_hours: (court.half_hours || []).map(normalizeHalfHour),
        lost_minutes: Number(court.lost_minutes) || 0,
    }))
}

// The rows of the day grid: every half-hour any court is open, in order
// (lunar-falcon-2). Courts usually share one day, but nothing says they must.
export function dayRows(courts) {
    const minutes = new Set()
    courts.forEach(court => (court.half_hours || []).forEach(half => minutes.add(half.start_minute)))
    return [...minutes].sort((a, b) => a - b)
}

export function normalizeMonth(json) {
    return ((json && json.days) || []).map(day => ({
        day: Number(day.day) || 0,
        taken: Number(day.taken) || 0,
        total: Number(day.total) || 0,
        held_minutes: Number(day.held_minutes) || 0,
        open_minutes: Number(day.open_minutes) || 0,
        lost_minutes: Number(day.lost_minutes) || 0,
    }))
}

function normalizePlayer(player) {
    return {
        user_id: player.user_id || '',
        username: player.username || '',
        full_name: player.full_name || '',
        is_guest: !!player.is_guest,
        guest_name: player.guest_name || '',
        is_member: !!player.is_member,
    }
}

export function normalizeBooking(json) {
    const booking = json && json.booking
    if (!booking) return null
    const game = booking.game
    return {
        id: booking.id || '',
        court_id: booking.court_id || '',
        court_number: Number(booking.court_number) || 0,
        court_name: booking.court_name || '',
        year: Number(booking.year) || 0,
        month: Number(booking.month) || 0,
        day: Number(booking.day) || 0,
        start_time: booking.start_time || '',
        start_minute: Number(booking.start_minute) || 0,
        kind: booking.kind || 'COURT_BOOKING_KIND_GAME',
        state: booking.state || 'CONSOLE_SLOT_STATE_UNKNOWN',
        label: booking.label || '',
        created_by_name: booking.created_by_name || '',
        created_at: Number(booking.created_at) || 0,
        group_id: booking.group_id || '',
        group_size: Number(booking.group_size) || 0,
        show_reason: !!booking.show_reason,
        assignees: (booking.assignees || []).map(a => ({
            club_user_id: a.club_user_id || '',
            name: a.name || '',
            is_coach: !!a.is_coach,
            is_admin: !!a.is_admin,
            is_staff: !!a.is_staff,
        })),
        game: game ? {
            id: game.id || '',
            organiser_id: game.organiser_id || '',
            organiser_name: game.organiser_name || '',
            players: (game.players || []).map(normalizePlayer),
            num_guests: Number(game.num_guests) || 0,
            start_at: Number(game.start_at) || 0,
            is_full: !!game.is_full,
            created_by_club: !!game.created_by_club,
        } : null,
        is_past: !!booking.is_past,
        lesson: normalizeLesson(booking.lesson),
    }
}

export function playerName(player) {
    if (player.is_guest) return player.guest_name || 'Invité'
    if (player.full_name) return player.full_name
    return player.username ? `@${player.username}` : 'Joueur'
}

// Who is in a game, guests included. Guests the server counts without listing
// them by name are shown as "Invité" so the list adds up to the seats taken.
export function gameLineup(game) {
    const names = game.players.map(playerName)
    const listedGuests = game.players.filter(player => player.is_guest).length
    for (let i = listedGuests; i < game.num_guests; i++) names.push('Invité')
    return names
}

export function seatsText(game) {
    return `${Math.min(gameLineup(game).length, 4)}/4 places`
}

// Players a cancellation notifies: the ones with an account.
export function notifiedPlayers(game) {
    return game ? game.players.filter(player => !player.is_guest).length : 0
}

export function cancelQuestion(booking) {
    const when = `${dayShort(booking)} à ${slotTime(booking.start_minute)}`
    const count = notifiedPlayers(booking.game)
    const who = count === 0
        ? ''
        : count === 1
            ? ' Le joueur sera prévenu par notification.'
            : ` Les ${count} joueurs seront prévenus par notification.`
    return `Annuler la partie du ${when} sur le terrain ${booking.court_number} ?${who} Cette action est définitive.`
}

export function cancelDoneText(notified) {
    if (!notified) return 'Partie annulée. Aucun joueur prévenu.'
    return `Partie annulée. ${notified} ${plural(notified, 'joueur prévenu', 'joueurs prévenus')}.`
}

export function unblockDoneText(removed) {
    if (!removed) return 'Aucun créneau débloqué.'
    return `${removed} ${plural(removed, 'créneau débloqué', 'créneaux débloqués')}.`
}

// A block's reason, shown to the club's members (cosmic-willow-3). Members
// only - the server sends it to nobody else - so the copy says "adhérents".
export const SHOW_REASON_LABEL = 'Afficher le motif aux adhérents'

// What a member reads in the app for this block: "Tournoi — Tournoi P250
// interne", or the kind alone without a label. The same text the app builds.
export function reasonPreview(kind, label) {
    const trimmed = (label || '').trim()
    return trimmed ? `${kindLabel(kind)} — ${trimmed}` : kindLabel(kind)
}

// Under the switch: what members will see, or that they see nothing.
export function showReasonDescription(kind, label, isShown) {
    if (!isShown) return 'Les adhérents voient « Indisponible » dans l\'app.'
    if (!kind) return 'Les adhérents verront la raison et le libellé au lieu de « Indisponible » dans l\'app.'
    return `Les adhérents verront « ${reasonPreview(kind, label)} » au lieu de « Indisponible » dans l'app.`
}

export function showReasonDoneText(isShown, updated) {
    const slots = `${updated} ${plural(updated, 'créneau', 'créneaux')}`
    return isShown
        ? `Motif visible par les adhérents (${slots}).`
        : `Motif masqué aux adhérents (${slots}).`
}

// "Bloqué par Marie Dupont le 21 octobre 2026"
export function blockedByText(name, createdAtSeconds) {
    const by = name ? ` par ${name}` : ''
    const on = createdAtSeconds ? ` le ${DATE_FORMATS.stamp.format(new Date(createdAtSeconds * 1000))}` : ''
    return `Bloqué${by}${on}`
}

// What a failed call says. 401 never gets here: it sends the reader back to
// the login page instead.
export function failureText(status) {
    switch (status) {
        case 403:
            return "Vous n'administrez pas ce club."
        case 409:
            return 'Ce créneau a changé entre-temps.'
        default:
            return 'Une erreur est survenue. Réessayez.'
    }
}

// ---------------------------------------------------------------------------
// Opening hours: one row per weekday, Monday first.
//
// Times are minutes since midnight, on the half hour, from 7:00 to midnight.
// A slot is 90 minutes long; the first starts at opening and the last is the
// last one over by closing - the rule the server builds the players' grid
// with, so the preview says what the players will see.

export const HOURS_FIRST = 7 * 60
export const HOURS_LAST = 24 * 60
export const SLOT_MINUTES = 90
const HOURS_STEP = 30

// The hours a club has until it sets its own: 8:00 - 23:00 every day.
const DEFAULT_DAY = { closed: false, open_minute: 8 * 60, close_minute: 23 * 60 }

// "9:00", "21:30", and "minuit" for the end of the day rather than "24:00".
export function hoursTime(minute) {
    return minute >= HOURS_LAST ? 'minuit' : slotTime(minute)
}

// Both selectors offer every half hour from 7:00 to midnight. A closing
// before the opening is said on the row rather than made impossible to pick,
// so changing the opening never quietly moves the closing.
export function hoursOptions() {
    const options = []
    for (let minute = HOURS_FIRST; minute <= HOURS_LAST; minute += HOURS_STEP) {
        options.push({ value: String(minute), label: hoursTime(minute) })
    }
    return options
}

// The slots one day's hours give: how many, and when the first and the last
// start. Closed, or open for less than a slot, is no slot at all.
export function daySlots(day) {
    if (day.closed) return { count: 0, first: 0, last: 0 }
    const count = Math.max(0, Math.floor((day.close_minute - day.open_minute) / SLOT_MINUTES))
    if (count === 0) return { count: 0, first: 0, last: 0 }
    return { count, first: day.open_minute, last: day.open_minute + (count - 1) * SLOT_MINUTES }
}

// What is wrong with one day's hours, or '' when nothing is. A closed day is
// always right, whatever its times say.
export function dayHoursError(day) {
    if (day.closed) return ''
    if (day.close_minute <= day.open_minute) return "La fermeture doit être après l'ouverture."
    if (day.close_minute - day.open_minute < SLOT_MINUTES) return "Aucun créneau — au moins 1 h 30 d'ouverture"
    return ''
}

export function hoursAreValid(days) {
    return days.every(day => dayHoursError(day) === '')
}

// "8 créneaux · 9:00 → 19:30": what the row says the day will look like.
export function slotsPreviewText(day) {
    if (day.closed) return 'Aucun créneau'
    const slots = daySlots(day)
    if (slots.count === 0) return "Aucun créneau — au moins 1 h 30 d'ouverture"
    if (slots.count === 1) return `1 créneau · ${slotTime(slots.first)}`
    return `${slots.count} créneaux · ${slotTime(slots.first)} → ${slotTime(slots.last)}`
}

function normalizeDayHours(day) {
    if (!day) return { ...DEFAULT_DAY }
    return {
        closed: !!day.closed,
        open_minute: Number(day.open_minute) || 0,
        close_minute: Number(day.close_minute) || 0,
    }
}

// Always seven days: a short answer is filled in with the default rather
// than leaving a weekday without its row.
export function normalizeHours(hours) {
    const days = (hours && hours.days) || []
    return {
        days: WEEKDAYS.map((name, index) => normalizeDayHours(days[index])),
        is_default: !!(hours && hours.is_default),
    }
}

export function sameHours(a, b) {
    return a.length === b.length && a.every((day, index) => (
        day.closed === b[index].closed
        && day.open_minute === b[index].open_minute
        && day.close_minute === b[index].close_minute
    ))
}

export function normalizeConflicts(json) {
    return ((json && json.conflicts) || []).map(conflict => ({
        booking_id: conflict.booking_id || '',
        court_id: conflict.court_id || '',
        court_number: Number(conflict.court_number) || 0,
        court_name: conflict.court_name || '',
        year: Number(conflict.year) || 0,
        month: Number(conflict.month) || 0,
        day: Number(conflict.day) || 0,
        start_time: conflict.start_time || '',
        start_minute: Number(conflict.start_minute) || 0,
        kind: conflict.kind || 'COURT_BOOKING_KIND_GAME',
        state: conflict.state || 'CONSOLE_SLOT_STATE_UNKNOWN',
        label: conflict.label || '',
        organiser_name: conflict.organiser_name || '',
    }))
}

// "3 réservations ne rentrent plus dans ces horaires"
export function conflictsTitle(count) {
    return count === 1
        ? '1 réservation ne rentre plus dans ces horaires'
        : `${count} réservations ne rentrent plus dans ces horaires`
}

// What holds a slot the new hours would drop, with the dot or the token the
// day grid shows for it: a game says who organised it and how far along it
// is, a block its kind and its label.
export function conflictLook(conflict) {
    const look = slotLook(conflict)
    if (conflict.state === 'CONSOLE_SLOT_STATE_LESSON') {
        return {
            text: `Cours — ${conflict.organiser_name || 'coach inconnu'}`,
            token: look.token,
            detail: conflict.label,
        }
    }
    if (conflict.state === 'CONSOLE_SLOT_STATE_GAME_PENDING' || conflict.state === 'CONSOLE_SLOT_STATE_GAME_CONFIRMED') {
        return {
            text: `Partie — ${conflict.organiser_name || 'organisateur inconnu'}`,
            dot: look.dot,
            detail: conflict.state === 'CONSOLE_SLOT_STATE_GAME_PENDING' ? 'en attente' : 'confirmée',
        }
    }
    return look
}

// ---------------------------------------------------------------------------
// Members: the club's roster and the players' requests to join it.
//
// A member travels as the server's ClubUser, snake_case, every field present.
// A birth date is a calendar day, "1987-03-14" or "", never a timestamp; it is
// shown the French way, "14/03/1987".

// "Marie Dupont", or the service name of a shared account, or a placeholder
// for a row with neither - which the server refuses to create, but an old
// row might still be one.
export function memberName(member) {
    const name = fullName(member || {})
    if (name) return name
    if (member && member.service_name) return member.service_name
    return 'Adhérent sans nom'
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

function realDay(text) {
    const match = ISO_DAY.exec(text || '')
    if (!match) return null
    const parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    const date = utc(parts)
    if (date.getUTCFullYear() !== parts.year || date.getUTCMonth() + 1 !== parts.month || date.getUTCDate() !== parts.day) return null
    return parts
}

// "14/03/1987"; nothing for no date, and the text as it came for one that
// is not a date at all, rather than hiding it.
export function birthDateText(iso) {
    if (!iso) return ''
    const parts = realDay(iso)
    if (!parts) return iso
    return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year}`
}

// Why the server would refuse this birth date, or '' when it would take it:
// optional, but when given a real day after 1 January 1900 and before today.
export function birthDateError(iso, today) {
    if (!iso) return ''
    const parts = realDay(iso)
    if (!parts) return "Ce n'est pas une date valide."
    if (utc(parts) <= utc({ year: 1900, month: 1, day: 1 })) return 'La date doit être postérieure au 1er janvier 1900.'
    if (utc(parts) >= utc(today)) return 'La date de naissance doit être passée.'
    return ''
}

// The requests to join first, then the members, each in the order the
// server gave them. matching_member_* only ever comes on a request.
export function splitMembers(list) {
    const pending = []
    const members = []
    for (const row of list || []) {
        if (row.is_pending) pending.push(row)
        else members.push(row)
    }
    return { pending, members }
}

export function pendingTitle(count) {
    return `Demandes en attente (${count})`
}

export function membersTitle(count) {
    return `Adhérents (${count})`
}

// The roster entry a request will be merged into when accepted.
export function matchingMemberText(request) {
    if (!request || !request.matching_member_name) return ''
    return `Correspond à l'adhérent ${request.matching_member_name} déjà inscrit — accepter reliera la demande à cette fiche`
}

export function refuseQuestion(request) {
    return `Refuser la demande de ${memberName(request)} ?`
}

export function removeQuestion(member) {
    return `Retirer ${memberName(member)} des adhérents du club ?`
}

// The roles a member holds, in the order they are shown.
export function memberRoles(member) {
    const roles = []
    if (member.is_admin) roles.push({ label: 'Admin', color: 'purple' })
    if (member.is_coach) roles.push({ label: 'Coach', color: 'teal' })
    if (member.is_staff) roles.push({ label: 'Personnel', color: 'orange' })
    return roles
}

// Only a member linked to a Padelr account can administer the club:
// authorisation goes by account, so the flag on an unlinked entry would
// promise something nothing could ever honour. The server refuses it too.
export function canBeAdmin(member) {
    return !!(member && member.user_id)
}

// The line under the Administrateur switch - the apps' wording.
export function adminNote(member, isAdmin) {
    if (!canBeAdmin(member)) return "Cet adhérent n'a pas de compte Padelr. Pour le nommer administrateur, ajoutez-le comme joueur Padelr : sa fiche sera reliée à son compte s'il utilise la même adresse e-mail."
    if (isAdmin) return 'Un administrateur peut gérer les pistes et les adhérents de ce club.'
    return ''
}

// What the form edits. Every field a string, so the inputs stay controlled.
export function memberDraft(member) {
    const m = member || {}
    return {
        first_name: m.first_name || '',
        last_name: m.last_name || '',
        birth_date: m.birth_date || '',
        email: m.email || '',
        phone: m.phone || '',
        is_coach: !!m.is_coach,
        is_admin: !!m.is_admin,
        is_staff: !!m.is_staff,
    }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Per field, why the form cannot be sent yet; an empty object when it can.
// With a picked player there is no email to check - the account's is used -
// and no name to insist on, since the account has one.
export function memberDraftErrors(draft, today, player) {
    const errors = {}
    if (!player && !draft.first_name.trim() && !draft.last_name.trim()) errors.name = 'Indiquez au moins un prénom ou un nom.'
    const email = draft.email.trim()
    if (!player && email && !EMAIL.test(email)) errors.email = "Cette adresse e-mail n'est pas valide."
    const birth = birthDateError(draft.birth_date, today)
    if (birth) errors.birth_date = birth
    return errors
}

function cleanDraft(draft) {
    return {
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        birth_date: draft.birth_date || '',
        email: draft.email.trim(),
        phone: draft.phone.trim(),
    }
}

// A new roster entry, with no role. Typed in, it has no account. For a picked
// player it carries their account and no email: the server takes the
// account's, and links the entry the club may already have under it.
export function memberToCreate(clubId, draft, player) {
    const entry = { club_id: clubId, ...cleanDraft(draft) }
    if (!player) return entry
    delete entry.email
    return { ...entry, user_id: player.id }
}

// The server $sets every editable field, so an update carries all of them:
// whatever the member had - service name, address, viewer flag - with the
// form's edits over it. What only the server sets stays out.
export function memberToUpdate(member, draft) {
    const kept = { ...member }
    delete kept.is_pending
    delete kept.matching_member_id
    delete kept.matching_member_name
    return {
        ...kept,
        ...cleanDraft(draft),
        is_coach: !!draft.is_coach,
        is_admin: canBeAdmin(member) && !!draft.is_admin,
        is_staff: !!draft.is_staff,
    }
}

// The French for a refused member call. 'link' is adding a picked player. A 400 on a removal is the server
// refusing an admin's removal of themselves - the one refusal there is when
// removing a member of a club the admin runs.
export function memberFailureText(action, status) {
    if (status === 400) {
        switch (action) {
            case 'delete':
                return 'Impossible de retirer cet adhérent : un administrateur ne peut pas se retirer lui-même du club. Un autre administrateur peut le faire.'
            case 'create':
            case 'update':
                return 'Le serveur a refusé ces informations. Vérifiez les champs, notamment la date de naissance.'
            case 'link':
                return "Ce joueur n'a pas pu être ajouté. Vérifiez la date de naissance, ou cherchez-le à nouveau."
            case 'validate':
                return "Cette demande n'a pas pu être acceptée."
            default:
                return 'Le serveur a refusé cette demande.'
        }
    }
    if (status === 409) {
        if (action === 'create') return 'Cet adhérent est déjà inscrit dans ce club.'
        if (action === 'link') return 'Ce joueur est déjà adhérent du club, ou a déjà demandé à le devenir.'
        return 'Cet adhérent a changé entre-temps. Rechargez la liste.'
    }
    if (status === 404) return "Cet adhérent n'existe plus. Rechargez la liste."
    if (status === 403) return "Vous n'administrez pas ce club."
    return 'Une erreur est survenue. Réessayez.'
}

// Adding a member: a Padelr player picked from a search, so the entry is
// linked to their account, or somebody typed in with no account.
export const ADD_MODES = [
    { value: 'player', label: 'Joueur Padelr' },
    { value: 'typed', label: 'Sans compte' },
]

// A picked player's name, split the way the form holds it: the first word is
// the first name, the rest the last name. The admin can correct either.
export function playerDraft(player) {
    const words = ((player && player.full_name) || '').trim().split(/\s+/).filter(Boolean)
    return {
        first_name: words[0] || '',
        last_name: words.slice(1).join(' '),
    }
}

// Who a search result is, under their name.
export function playerLine(player) {
    const parts = []
    if (player.username) parts.push(`@${player.username}`)
    if (player.level) parts.push(`Niveau ${player.level}`)
    if (player.is_member) parts.push('Déjà adhérent')
    return parts.join(' · ')
}

export const ACCOUNT_EMAIL_NOTE = "L'e-mail de son compte Padelr sera utilisé."

// ---------------------------------------------------------------------------
// "Créer une partie…": a game the club creates on one free slot, with up to
// four players picked from its roster, from Padelr, or typed in as guests.
//
// A place is { kind, userId, clubUserId, name }:
//   - member: someone on the roster. userId is their account when the entry
//     is linked to one; without it they play as a guest named after them.
//   - player: a Padelr account found by the player search.
//   - guest: a name typed in; the place is empty until the name is.
// One of the players with an account organises the game: they own it in the
// app (crimson-iris-2).

export const GAME_PLACES = 4
export const GUEST_NAME_MAX = 40

export const PLACE_KINDS = [
    { value: 'member', label: 'Adhérent du club' },
    { value: 'player', label: 'Joueur Padelr' },
    { value: 'guest', label: 'Invité' },
]

export function emptyPlaces() {
    return Array.from({ length: GAME_PLACES }, () => ({ kind: '', userId: '', clubUserId: '', name: '' }))
}

export function memberPlace(member) {
    return { kind: 'member', userId: member.user_id || '', clubUserId: member.id, name: memberName(member) }
}

export function playerPlace(player) {
    return { kind: 'player', userId: player.id, clubUserId: '', name: player.full_name || `@${player.username}` }
}

export function isPlaceFilled(place) {
    if (place.kind === 'guest') return place.name.trim().length > 0
    if (place.kind === 'member') return !!place.clubUserId
    if (place.kind === 'player') return !!place.userId
    return false
}

// What the place reads as once filled.
export function placeDescription(place) {
    if (place.kind === 'member') return place.userId ? 'Adhérent · compte Padelr' : 'Adhérent · sans compte, joue comme invité'
    if (place.kind === 'player') return 'Joueur Padelr'
    return 'Invité'
}

// The players who can organise: one per account, in the order picked.
export function organiserChoices(places) {
    const seen = new Set()
    const out = []
    for (const place of places) {
        if (!isPlaceFilled(place) || !place.userId || seen.has(place.userId)) continue
        seen.add(place.userId)
        out.push({ userId: place.userId, name: place.name })
    }
    return out
}

// The organiser kept while still playing, else the first who can.
export function chooseOrganiser(places, current) {
    const choices = organiserChoices(places)
    if (choices.some(c => c.userId === current)) return current
    return choices.length > 0 ? choices[0].userId : ''
}

// Why "Créer" cannot be pressed yet, the first reason only; '' when it can.
export function gameDraftError(places, organiserId) {
    const filled = places.filter(isPlaceFilled)
    if (filled.length === 0) return 'Ajoutez au moins un joueur.'
    const accounts = new Set()
    const members = new Set()
    for (const place of filled) {
        if (place.kind === 'guest' && place.name.trim().length > GUEST_NAME_MAX) {
            return `Le nom d'un invité fait ${GUEST_NAME_MAX} caractères au plus.`
        }
        if (place.clubUserId) {
            if (members.has(place.clubUserId)) return `${place.name} est choisi deux fois.`
            members.add(place.clubUserId)
        }
        if (place.userId) {
            if (accounts.has(place.userId)) return `${place.name} est choisi deux fois.`
            accounts.add(place.userId)
        }
    }
    if (accounts.size === 0) return 'Il faut au moins un joueur avec un compte Padelr pour organiser la partie.'
    if (!accounts.has(organiserId)) return "Choisissez l'organisateur."
    return ''
}

// The request's players and organiser. A member goes by their roster entry,
// so the server decides how they play.
export function gameToCreate(places, organiserId) {
    const players = []
    for (const place of places) {
        if (!isPlaceFilled(place)) continue
        if (place.kind === 'member') players.push({ club_user_id: place.clubUserId })
        else if (place.kind === 'player') players.push({ user_id: place.userId })
        else players.push({ guest_name: place.name.trim() })
    }
    return { organiser_id: organiserId, players }
}

export function createGameDoneText(notified) {
    if (!notified) return 'Partie créée.'
    return `Partie créée. ${notified} ${plural(notified, 'joueur prévenu', 'joueurs prévenus')}.`
}

export function createGameFailureText(status) {
    switch (status) {
        case 400:
            return 'Le serveur a refusé cette partie. Vérifiez les joueurs et le créneau.'
        case 409:
            return "Ce créneau vient d'être pris."
        default:
            return failureText(status)
    }
}

// A roster entry the game can take: a member, not a request to join.
export function pickableMembers(list) {
    return splitMembers(list).members
}

// Who works on a block (azure-zephyr-2): club members picked from the roster
// by name, sent as their roster ids.

// "Jean Entretien, Hugo Coach", or a dash for nobody.
export function assigneesText(assignees) {
    const names = (assignees || []).map(a => a.name).filter(Boolean)
    return names.length > 0 ? names.join(', ') : '—'
}

// What the picker remembers of a member: enough to show them and send them.
export function assigneeOf(member) {
    return {
        club_user_id: member.id,
        name: memberName(member),
        is_coach: !!member.is_coach,
        is_admin: !!member.is_admin,
        is_staff: !!member.is_staff,
    }
}

// The roles an assignee or a person in the report holds, as tokens.
export function personRoles(person) {
    return memberRoles(person || {})
}

// The occupancy report (azure-zephyr-2): who held the club's courts over a
// range of days, and for what. Computed by the server on every call.

// Every kind the report totals, in the server's order, with how it is named.
export const OCCUPANCY_KINDS = [
    { value: 'COURT_BOOKING_KIND_GAME', label: 'Parties' },
    { value: 'COURT_BOOKING_KIND_LESSON', label: 'Cours' },
    { value: 'COURT_BOOKING_KIND_EVENT', label: 'Événements' },
    { value: 'COURT_BOOKING_KIND_TOURNAMENT', label: 'Tournois' },
    { value: 'COURT_BOOKING_KIND_MAINTENANCE', label: 'Maintenance' },
]

// The longest range the server takes, in days.
export const OCCUPANCY_MAX_DAYS = 366

// "18 %", or "—" when there was nothing to take.
export function shareText(part, whole) {
    const w = Number(whole) || 0
    if (w <= 0) return '—'
    return `${Math.round(((Number(part) || 0) / w) * 100)} %`
}

// The calendar month a day is in, and the one before it.
export function monthRange(today) {
    const first = { year: today.year, month: today.month, day: 1 }
    const last = addDays(addMonths(first, 1), -1)
    return { from: first, to: last }
}

export function lastMonthRange(today) {
    return monthRange(addMonths({ year: today.year, month: today.month, day: 1 }, -1))
}

function dayCount(from, to) {
    return Math.round((utc(to) - utc(from)) / 86400000) + 1
}

// The range in the address, "?du=2026-10-01&au=2026-10-31", else this month.
// A range the server would refuse falls back to this month rather than
// opening on an error.
export function readOccupancyRange(du, au, today) {
    const from = parseIsoDay(du)
    const to = parseIsoDay(au)
    if (from && to && !occupancyRangeError(from, to)) return { from, to }
    return monthRange(today)
}

// Why the server would refuse this range, or '' when it would take it.
export function occupancyRangeError(from, to) {
    if (!from || !to) return 'Choisissez une date de début et une date de fin.'
    if (utc(to) < utc(from)) return 'La date de fin doit suivre la date de début.'
    if (dayCount(from, to) > OCCUPANCY_MAX_DAYS) return `La période ne peut pas dépasser ${OCCUPANCY_MAX_DAYS} jours.`
    return ''
}

// "du 1er octobre au 31 octobre 2026", and "octobre 2026" for a whole month.
export function occupancyRangeTitle(from, to) {
    const whole = monthRange(from)
    if (sameDay(whole.from, from) && sameDay(whole.to, to)) return monthTitle(from)
    const day = parts => (parts.day === 1 ? '1er' : String(parts.day))
    const month = parts => new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' }).format(utc(parts))
    const start = from.year === to.year ? `${day(from)} ${month(from)}` : `${day(from)} ${month(from)} ${from.year}`
    return `du ${start} au ${day(to)} ${month(to)} ${to.year}`
}

// The minutes a person or the courts spent on one kind; 0 for none.
export function kindMinutes(byKind, kind) {
    const found = (byKind || []).find(k => (k.kind || 'COURT_BOOKING_KIND_GAME') === kind)
    return found ? Number(found.minutes) || 0 : 0
}

// "Coach · Personnel", or "Adhérent", or "Hors club" for a player not on the
// roster.
export function personRoleText(person) {
    if (!person.is_member) return 'Hors club'
    const roles = personRoles(person).map(r => r.label)
    return roles.length > 0 ? roles.join(' · ') : 'Adhérent'
}

// The report as rows, normalised once: numbers as numbers, kinds spelled out.
export function normalizeOccupancy(json) {
    const r = json || {}
    return {
        courts: Number(r.courts) || 0,
        openMinutes: Number(r.open_minutes) || 0,
        heldMinutes: Number(r.held_minutes) || 0,
        byKind: OCCUPANCY_KINDS.map(k => ({ ...k, minutes: kindMinutes(r.by_kind, k.value) })),
        people: (r.people || []).map(p => ({
            ...p,
            minutes: Number(p.minutes) || 0,
            bookings: Number(p.bookings) || 0,
        })),
    }
}

// Only the staff: coaches, admins and Personnel (cosmic-cedar-3).
export function isStaffPerson(person) {
    return !!(person.is_member && (person.is_coach || person.is_admin || person.is_staff))
}

function csvField(value) {
    const text = String(value === undefined || value === null ? '' : value)
    return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

// Hours with a comma, as a French spreadsheet reads a number: "1,5".
function csvHours(minutes) {
    return String(Math.round((Number(minutes) || 0) / 6) / 10).replace('.', ',')
}

// The report as a CSV a French spreadsheet opens as columns: ";" between
// fields, hours as decimal numbers with a comma, and a byte-order mark so
// the accents survive. One row per person, then the courts' totals.
export function occupancyCsv(report, from, to) {
    const header = ['Nom', 'Rôle', 'Heures', 'Réservations', ...OCCUPANCY_KINDS.map(k => `${k.label} (h)`)]
    const rows = [header]
    for (const p of report.people) {
        rows.push([
            p.name || 'Sans nom',
            personRoleText(p),
            csvHours(p.minutes),
            p.bookings,
            ...OCCUPANCY_KINDS.map(k => csvHours(kindMinutes(p.by_kind, k.value))),
        ])
    }
    rows.push([])
    rows.push(['Période', `${isoDay(from)} au ${isoDay(to)}`])
    rows.push(['Terrains', report.courts])
    rows.push(['Heures d\'ouverture', csvHours(report.openMinutes)])
    rows.push(['Heures occupées', csvHours(report.heldMinutes)])
    for (const k of report.byKind) rows.push([`${k.label} (h)`, csvHours(k.minutes)])
    return `﻿${rows.map(row => row.map(csvField).join(';')).join('\r\n')}\r\n`
}

// "occupation-2026-10-01-au-2026-10-31.csv"
export function occupancyFileName(from, to) {
    return `occupation-${isoDay(from)}-au-${isoDay(to)}.csv`
}

// After the intervenants of a block were changed: on how many slots.
export function assigneesDoneText(updated) {
    return updated > 1 ? `Intervenants modifiés sur ${updated} créneaux.` : 'Intervenants modifiés.'
}
